import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { can } from "@/lib/permissions";
import AppLayout from "@/components/AppLayout";
import LoadingScreen from "@/components/LoadingScreen";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Swing from "@/pages/Swing";
import SwingHistory from "@/pages/SwingHistory";
import ShiftSummary from "@/pages/ShiftSummary";
import SuperintendentCentre from "@/pages/SuperintendentCentre";
import RegisterUser from "@/pages/RegisterUser";
import Insights from "@/pages/Insights";
import Handovers from "@/pages/Handovers";
import HandoverDetailPage from "@/pages/HandoverDetailPage";
import Breakdowns from "@/pages/Breakdowns";
import BreakdownDetailPage from "@/pages/BreakdownDetailPage";
import Inspections from "@/pages/Inspections";
import InspectionDetailPage from "@/pages/InspectionDetailPage";
import PPFs from "@/pages/PPFs";
import Hazards from "@/pages/Hazards";
import Assets from "@/pages/Assets";
import FaultFinder from "@/pages/FaultFinder";
import AdminUsers from "@/pages/AdminUsers";
import AdminSites from "@/pages/AdminSites";
import OwnerCenter from "@/pages/OwnerCenter";
import SitesPage from "@/pages/owner/SitesPage";
import PeoplePage from "@/pages/owner/PeoplePage";
import AssetsPage from "@/pages/owner/AssetsPage";
import InspectionBuilderPage from "@/pages/owner/InspectionBuilderPage";
import Crews from "@/pages/owner/Crews";
import SiteConfig from "@/pages/owner/SiteConfig";
import CustomModules from "@/pages/owner/CustomModules";
import AllReports from "@/pages/owner/AllReports";
import BackupsCentre from "@/pages/BackupsCentre";
import Profile from "@/pages/Profile";
import "@/App.css";

function Protected({ children, adminOnly = false, ...rest }) {
  const { user } = useAuth();
  if (user === undefined) return <LoadingScreen />;
  if (user === null) return <Navigate to="/login" replace />;
  if (adminOnly) {
    // Legacy adminOnly flag → supervisor tier minimum.
    if (!can(user, "crewDashboard")) return <Navigate to="/" replace />;
  }
  if (rest.cap && !can(user, rest.cap)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function ProtectedCap({ cap, children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  if (!can(user, cap)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <Protected>
            <AppLayout>
              <ErrorBoundary>
                <Routes>
                <Route path="/" element={<Swing />} />
                <Route path="/home" element={<Swing />} />
                <Route path="/swing" element={<Swing />} />
                <Route path="/swing-history" element={<SwingHistory />} />
                <Route path="/shift-summary" element={<ShiftSummary />} />
                <Route path="/command-centre" element={<ProtectedCap cap="crewDashboard"><SuperintendentCentre /></ProtectedCap>} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/register" element={<ProtectedCap cap="registerUser"><RegisterUser /></ProtectedCap>} />
                <Route path="/insights" element={<ProtectedCap cap="insights"><Insights /></ProtectedCap>} />
                <Route path="/handovers" element={<Handovers />} />
                <Route path="/handovers/:id" element={<HandoverDetailPage />} />
                <Route path="/breakdowns" element={<Breakdowns />} />
                <Route path="/breakdowns/:id" element={<BreakdownDetailPage />} />
                <Route path="/inspections" element={<Inspections />} />
                <Route path="/inspections/:id" element={<InspectionDetailPage />} />
                <Route path="/ppfs" element={<PPFs />} />
                <Route path="/hazards" element={<Hazards />} />
                <Route path="/assets" element={<Assets />} />
                <Route path="/fault-finder" element={<FaultFinder />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/owner" element={<ProtectedCap cap="ownerCentre"><OwnerCenter /></ProtectedCap>} />
                <Route path="/owner/sites" element={<ProtectedCap cap="manageSites"><SitesPage /></ProtectedCap>} />
                <Route path="/owner/site-config" element={<ProtectedCap cap="siteConfig"><SiteConfig /></ProtectedCap>} />
                <Route path="/owner/people" element={<ProtectedCap cap="managePeople"><PeoplePage /></ProtectedCap>} />
                <Route path="/owner/crews" element={<ProtectedCap cap="crews"><Crews /></ProtectedCap>} />
                <Route path="/owner/assets" element={<ProtectedCap cap="manageAssets"><AssetsPage /></ProtectedCap>} />
                <Route path="/owner/inspection-builder" element={<ProtectedCap cap="inspectionBuilder"><InspectionBuilderPage /></ProtectedCap>} />
                <Route path="/owner/custom-modules" element={<ProtectedCap cap="customModules"><CustomModules /></ProtectedCap>} />
                <Route path="/owner/reports" element={<ProtectedCap cap="allReports"><AllReports /></ProtectedCap>} />
                <Route path="/owner/backups" element={<ProtectedCap cap="manageAssets"><BackupsCentre /></ProtectedCap>} />
                <Route path="/admin/users" element={<ProtectedCap cap="managePeople"><AdminUsers /></ProtectedCap>} />
                <Route path="/admin/sites" element={<ProtectedCap cap="manageSites"><AdminSites /></ProtectedCap>} />
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </ErrorBoundary>
            </AppLayout>
          </Protected>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SiteProvider>
            <AppRoutes />
          </SiteProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
